<template>
    <Head title="Work Order Notification & Routing Policies" />

    <div class="py-6">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <!-- Header -->
                <div class="mb-6">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <div>
                                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Work Order Notification & Routing Policies</h1>
                                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Define who gets notified when work orders are created based on type, category, and priority</p>
                            </div>
                            <InfoButton title="Notification & Routing Policies Management" variant="default">
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                    Work Order Notification & Routing Policies automatically notify specific users or roles when work orders
                                    matching certain criteria are created. This ensures the right people are immediately informed
                                    about new work, reducing response times and improving coordination. Policies use matching rules
                                    based on work order type, service category, and priority to determine who receives email and
                                    in-app notifications.
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                    • <strong>Add Notification & Routing Policy:</strong> Create policies with matching criteria (type,
                                      category, priority) and assign recipients (users or roles)<br>
                                    • <strong>Search:</strong> Filter policies by name using the search box<br>
                                    • <strong>Status Filter:</strong> View active or inactive policies<br>
                                    • <strong>Criteria Column:</strong> Shows the matching conditions - more specific policies
                                      take precedence<br>
                                    • <strong>Recipients Column:</strong> Shows count of users or roles that will be notified<br>
                                    • <strong>View:</strong> See policy details including full recipient list and criteria<br>
                                    • <strong>Activate/Deactivate:</strong> Toggle whether policy sends notifications<br>
                                    • <strong>Delete:</strong> Remove policies no longer needed
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                    • <strong>Start with default policies</strong> - Create general policies for each priority
                                      level first<br>
                                    • <strong>Use role-based notifications</strong> - Roles automatically include all users with
                                      that role, even new hires<br>
                                    • <strong>Specificity matching</strong> - Policies with more criteria take precedence (e.g.,
                                      "HVAC + Urgent" over "Urgent")<br>
                                    • <strong>Test notifications</strong> - Create a test work order to verify the correct
                                      recipients are notified<br>
                                    • <strong>Avoid notification fatigue</strong> - Don't create too many overlapping policies
                                      or people will be overwhelmed<br>
                                    • <strong>Review regularly</strong> - Update policies when team structure changes
                                </p>
                            </InfoButton>
                        </div>
                        <Link
                            :href="route('settings.maintenance.notification-policies.create')"
                            class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600 focus:bg-blue-700 active:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition ease-in-out duration-150"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add Notification & Routing Policy
                        </Link>
                    </div>
                </div>

                <!-- Filters -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Search -->
                        <div>
                            <label for="search" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                            <input
                                id="search"
                                v-model="filters.search"
                                type="text"
                                placeholder="Search by name..."
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                @input="debouncedSearch"
                            />
                        </div>

                        <!-- Active Filter -->
                        <div>
                            <label for="active" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                            <select
                                id="active"
                                v-model="filters.active"
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                @change="search"
                            >
                                <option value="">All</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>

                        <!-- Reset Button -->
                        <div class="flex items-end">
                            <button
                                @click="resetFilters"
                                class="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                                Reset Filters
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Policies Table -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Policy Name
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Criteria
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Recipients
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="policy in policies.data" :key="policy.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td class="px-6 py-4">
                                    <div class="flex items-center">
                                        <div>
                                            <div class="text-sm font-medium text-gray-900 dark:text-white">
                                                {{ policy.name }}
                                            </div>
                                            <div v-if="policy.description" class="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                                                {{ policy.description }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <div class="text-sm text-gray-900 dark:text-white">
                                        <div v-if="policy.work_order_type" class="mb-1">
                                            <span class="font-medium">Type:</span> {{ formatEnumValue(policy.work_order_type) }}
                                        </div>
                                        <div v-if="policy.service_category" class="mb-1">
                                            <span class="font-medium">Category:</span> {{ policy.service_category.name }}
                                        </div>
                                        <div v-if="policy.location" class="mb-1">
                                            <span class="font-medium">Location:</span> {{ policy.location.name }}
                                        </div>
                                        <div v-if="policy.asset" class="mb-1">
                                            <span class="font-medium">Asset:</span> {{ policy.asset.name }}
                                        </div>
                                        <div v-if="policy.priority" class="mb-1">
                                            <span class="font-medium">Priority:</span> {{ formatEnumValue(policy.priority) }}
                                        </div>
                                        <div v-if="!policy.work_order_type && !policy.service_category && !policy.location && !policy.asset && !policy.priority" class="text-gray-500 dark:text-gray-400 italic">
                                            All work orders (default)
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    <div class="flex items-center">
                                        <svg v-if="policy.recipient_type === 'users'" class="w-4 h-4 mr-1 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        <svg v-else class="w-4 h-4 mr-1 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        <span>{{ policy.recipient_count }} {{ policy.recipient_type === 'users' ? 'user' : 'role' }}{{ policy.recipient_count !== 1 ? 's' : '' }}</span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                            policy.is_active
                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                                        ]"
                                    >
                                        {{ policy.is_active ? 'Active' : 'Inactive' }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div class="flex items-center justify-end space-x-2">
                                        <Link
                                            :href="route('settings.maintenance.notification-policies.show', policy.id)"
                                            class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                        >
                                            View
                                        </Link>
                                        <button
                                            @click="toggleActive(policy)"
                                            class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                        >
                                            {{ policy.is_active ? 'Deactivate' : 'Activate' }}
                                        </button>
                                        <button
                                            @click="deletePolicy(policy)"
                                            class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="policies.data.length === 0">
                                <td colspan="5" class="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                                    No notification and routing policies found. Create your first policy to define who gets notified and routed when work orders are created.
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- Pagination -->
                    <div v-if="policies.data.length > 0" class="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                        <Pagination :links="policies.links" />
                    </div>
                </div>

                <!-- Info Box -->
                <div class="mt-6 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-500 rounded-lg p-4">
                    <div class="flex">
                        <div class="flex-shrink-0">
                            <svg class="h-5 w-5 text-blue-400 dark:text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <div class="ml-3 flex-1">
                            <p class="text-sm text-blue-700 dark:text-blue-300">
                                <strong>How notification and routing policies work:</strong> When a work order is created, the system automatically selects the most specific matching policy based on the work order's type, category, and priority. More specific policies take precedence over general ones. Recipients will receive both email and in-app notifications, and work orders will be routed to the designated Maintenance Managers.
                            </p>
                        </div>
                    </div>
                </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { debounce } from 'lodash';
import Pagination from '@/Components/Pagination.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    policies: Object,
    filters: Object,
});

const toast = useToast();

const filters = ref({
    search: props.filters.search || '',
    active: props.filters.active || '',
});

const search = () => {
    router.get(route('settings.maintenance.notification-policies.index'), filters.value, {
        preserveState: true,
        preserveScroll: true,
    });
};

const debouncedSearch = debounce(search, 300);

const resetFilters = () => {
    filters.value = {
        search: '',
        active: '',
    };
    search();
};

const formatEnumValue = (value) => {
    if (!value) return '';
    return value.charAt(0).toUpperCase() + value.slice(1);
};

const toggleActive = (policy) => {
    router.post(
        route('settings.maintenance.notification-policies.toggleActive', policy.id),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`Policy ${policy.is_active ? 'deactivated' : 'activated'} successfully`);
            },
            onError: () => {
                toast.error('Failed to update policy status');
            },
        }
    );
};

const deletePolicy = (policy) => {
    if (!confirm(`Are you sure you want to delete "${policy.name}"? This action cannot be undone.`)) {
        return;
    }

    router.delete(route('settings.maintenance.notification-policies.destroy', policy.id), {
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Policy deleted successfully');
        },
        onError: (errors) => {
            toast.error(errors.error || 'Failed to delete policy');
        },
    });
};
</script>
